import { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import apiClient from '../utils/apiClient'

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const toast = useToast()

  useEffect(() => {
    // Check if user is logged in on mount
    try {
      const userData = sessionStorage.getItem('user')
      const token = sessionStorage.getItem('token')
      
      if (userData && token) {
        setUser(JSON.parse(userData))
      }
    } catch (error) {
      console.error('Error parsing stored user data:', error)
      // Clear corrupted data
      sessionStorage.removeItem('user')
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('refreshToken')
    }
    setLoading(false)
  }, [])

  const login = async (email, password) => {
    try {
      setLoading(true)
      
      // Use apiClient instead of direct fetch
      const data = await apiClient('/users/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      })
      
      // Handle different response structures
      const userData = data.user || data.data?.user || data
      const token = data.token || data.data?.token
      
      if (!userData || !token) {
        throw new Error('Invalid response from server')
      }
      
      sessionStorage.setItem('token', token)
      sessionStorage.setItem('user', JSON.stringify(userData))
      setUser(userData)
      
      toast({
        title: 'Login successful',
        description: `Welcome back, ${userData.name}!`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      
      navigate('/')
    } catch (error) {
      toast({
        title: 'Login failed',
        description: error.message || 'Login failed',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('user')
    sessionStorage.removeItem('refreshToken')
    setUser(null)
    
    toast({
      title: 'Logged out',
      description: 'You have been successfully logged out',
      status: 'info',
      duration: 3000,
      isClosable: true
    })
    
    navigate('/login')
  }

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}